import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Alert, Button, Drawer, Input, Space, Tabs, Tag, Typography } from 'antd';
import { MailOutlined, UserAddOutlined } from '@ant-design/icons';
import type { WorkItemDetail } from '@/api/types';
import { assignWorkItem, getWorkItem } from '@/api/workItems';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { useAuth } from '@/auth/OlAuthContext';
import { EntitySummaryCard } from '@/entity/EntitySummaryCard';
import { RfiComposer } from '@/rfi/RfiComposer';
import { AgeBadge, PriorityBadge, SlaBadge, WorkItemStatusTag, WorkItemTypeBadge } from './badges';
import {
  DecisionsPanel,
  PriorDispositionsPanel,
  RunSummaryPanel,
  WhyItFiredPanel,
} from './contextPanels';
import { DispositionPanel } from './DispositionPanel';
import { drawerAction, isTypingTarget } from './keymap';
import { erasedOr } from '@/lib/erased';

const { Text } = Typography;

/** The drawer's context tabs, in the order `[` and `]` walk them. */
const TAB_KEYS = ['overview', 'evidence', 'decisions'] as const;

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="ol-section">
        {title}
        {count != null && <span className="ol-section__count">· {count}</span>}
      </div>
      {children}
    </div>
  );
}

interface Props {
  itemId: string | null;
  onClose: () => void;
  /** Refresh the queue after an assign/disposition. */
  onChanged: () => void;
}

export function WorkItemDrawer({ itemId, onClose, onChanged }: Props) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState<WorkItemDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [rfiOpen, setRfiOpen] = useState(false);
  const [tab, setTab] = useState('overview');

  const reload = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setUnavailable(false);
    try {
      setDetail(await getWorkItem(itemId));
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
    // message is stable from App context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  useEffect(() => {
    setDetail(null);
    setAssignee('');
    setTab('overview'); // every item opens on the deciding view, never on the last tab you left
    if (itemId) void reload();
  }, [itemId, reload]);

  // `[` / `]` walk the context tabs. Wraps, so a repeated press cycles rather than dead-ending;
  // suppressed while typing, like every other single-key shortcut.
  useEffect(() => {
    if (itemId == null) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      const action = drawerAction(e.key);
      if (!action || action.kind !== 'tab') return;
      e.preventDefault();
      setTab((current) => {
        const i = Math.max(0, TAB_KEYS.indexOf(current as (typeof TAB_KEYS)[number]));
        return TAB_KEYS[(i + action.delta + TAB_KEYS.length) % TAB_KEYS.length];
      });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [itemId]);

  const doAssign = useCallback(
    async (who?: string) => {
      if (!itemId) return;
      setAssigning(true);
      try {
        await assignWorkItem(itemId, who);
        message.success(who ? `Assigned to ${who}` : 'Assigned to you');
        setAssignee('');
        await reload();
        onChanged();
      } catch (e) {
        const n = normalizeApiError(e);
        if (n.code === 'ALREADY_DONE') {
          message.warning('This item was already completed. Refreshing.');
          await reload();
          onChanged();
        } else if (isNotAvailable(n)) {
          message.info('The assign endpoint is not live yet (Track A pending).');
        } else {
          message.error(n.message);
        }
      } finally {
        setAssigning(false);
      }
    },
    [itemId, reload, onChanged, message],
  );

  const openApprovals = useCallback(
    (flowId: string, approvalId?: string) => {
      onClose();
      navigate(`/flows/${flowId}${approvalId ? `?approval=${encodeURIComponent(approvalId)}` : ''}`);
    },
    [navigate, onClose],
  );

  const openEntity = useCallback(
    (entityId: string) => {
      onClose();
      navigate(`/entities/${encodeURIComponent(entityId)}`);
    },
    [navigate, onClose],
  );

  const item = detail?.item;
  const context = detail?.context;
  const mine =
    item?.assignee != null &&
    [user?.sub, user?.email, user?.name].filter(Boolean).includes(item.assignee);

  return (
    <Drawer
      open={itemId != null}
      onClose={onClose}
      width={620}
      title={item?.title ?? (loading ? 'Loading…' : 'Work item')}
      loading={loading && !detail}
    >
      {unavailable && (
        <Alert
          type="info"
          showIcon
          style={{ borderRadius: 8 }}
          message="Work-item detail is not live yet"
          description="The /v1/work-items/{id} endpoint (Track A, PS-3) supplies the per-type context bundle — run summary, evidence, decisions, why-it-fired, and prior-disposition memory — that this drawer renders."
        />
      )}

      {item && context && (
        <div>
          {/* Meta header */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 6 }}>
            <WorkItemTypeBadge type={item.type} />
            <WorkItemStatusTag status={item.status} />
            <PriorityBadge priority={item.priority} />
            <SlaBadge slaDueAt={item.slaDueAt} createdAt={item.createdAt} />
            <AgeBadge createdAt={item.createdAt} />
          </div>
          <div className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)', marginBottom: 12 }}>
            ref: {item.refId}
            {item.spokeId ? ` · spoke: ${item.spokeId}` : ''}
          </div>

          {/* Assignment */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Assignee:{' '}
              <Text style={{ fontSize: 12 }}>{item.assignee ?? 'unassigned'}</Text>
              {mine && (
                <Tag bordered={false} color="processing" style={{ marginInlineStart: 6, fontSize: 10 }}>
                  you
                </Tag>
              )}
            </Text>
            <Space.Compact>
              <Button
                icon={<UserAddOutlined />}
                loading={assigning}
                disabled={Boolean(mine)}
                onClick={() => doAssign(undefined)}
              >
                Assign to me
              </Button>
            </Space.Compact>
            <Space.Compact>
              <Input
                placeholder="Assign to…"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                style={{ width: 160 }}
              />
              <Button loading={assigning} disabled={!assignee.trim()} onClick={() => doAssign(assignee.trim())}>
                Assign
              </Button>
            </Space.Compact>
          </div>

          {/* Context TABS (OPSFL-POLISH-1): three fixed tabs, `[` and `]` walk them. The default
              tab carries everything needed to DECIDE — why-it-fired stays visible the moment the
              item opens, never behind a click. Evidence and decisions are reference material and
              earn their own tabs so the deciding view stays short. */}
          <Tabs
            activeKey={tab}
            onChange={setTab}
            size="small"
            items={[
              {
                key: 'overview',
                label: (
                  <span>
                    Overview{' '}
                    <span className="ol-kbd" aria-hidden>
                      [
                    </span>
                  </span>
                ),
                children: (
                  <div>
                    {/* SIGNAL_REVIEW: why-it-fired ALWAYS visible + prior-disposition memory. */}
                    {item.type === 'SIGNAL_REVIEW' && context.whyItFired && (
                      <div style={{ marginBottom: 18 }}>
                        <WhyItFiredPanel why={context.whyItFired} />
                      </div>
                    )}
                    {context.priorDispositions && context.priorDispositions.length > 0 && (
                      <Section title="Prior-disposition memory" count={context.priorDispositions.length}>
                        <PriorDispositionsPanel priors={context.priorDispositions} />
                      </Section>
                    )}

                    {/* APPROVAL summary. */}
                    {item.type === 'APPROVAL' && context.approvalSummary && (
                      <Section title="Approval">
                        <div className="ol-card">
                          <div className="ol-kv">
                            <span className="ol-kv__k">Flow</span>
                            <span className="ol-kv__v ol-mono">{context.approvalSummary.flowId}</span>
                            {context.approvalSummary.version != null && (
                              <>
                                <span className="ol-kv__k">Version</span>
                                <span className="ol-kv__v">v{context.approvalSummary.version}</span>
                              </>
                            )}
                            {context.approvalSummary.changeClass && (
                              <>
                                <span className="ol-kv__k">Change</span>
                                <span className="ol-kv__v">
                                  <Tag bordered={false}>{context.approvalSummary.changeClass}</Tag>
                                </span>
                              </>
                            )}
                            {context.approvalSummary.proposedBy && (
                              <>
                                <span className="ol-kv__k">Proposed by</span>
                                <span className="ol-kv__v">{context.approvalSummary.proposedBy}</span>
                              </>
                            )}
                            {context.approvalSummary.state && (
                              <>
                                <span className="ol-kv__k">State</span>
                                <span className="ol-kv__v">{context.approvalSummary.state}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </Section>
                    )}

                    {/* RFI_FOLLOWUP status. */}
                    {item.type === 'RFI_FOLLOWUP' && context.rfi && (
                      <Section title="RFI">
                        <div className="ol-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 12.5 }}>
                              {erasedOr(context.rfi.subject ?? 'Request for information')}
                            </span>
                            <Tag bordered={false}>{context.rfi.status}</Tag>
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', marginTop: 5 }}>
                            {context.rfi.channel} · {context.rfi.locale} · {context.rfi.asks.length} ask(s)
                          </div>
                        </div>
                      </Section>
                    )}

                    {/* Run summary (HUMAN_TASK / others with a run). */}
                    {context.runSummary && (
                      <Section title="Run">
                        <RunSummaryPanel run={context.runSummary} />
                      </Section>
                    )}

                    {/* Entity summary. */}
                    {context.entitySummary && (
                      <Section title="Entity">
                        <EntitySummaryCard
                          summary={context.entitySummary}
                          onOpen={() => openEntity(context.entitySummary!.entityId)}
                        />
                      </Section>
                    )}

                    {/* RFI launch (from a work item, when it targets an entity). */}
                    {item.entityId && item.type !== 'RFI_FOLLOWUP' && (
                      <Button icon={<MailOutlined />} onClick={() => setRfiOpen(true)}>
                        Raise RFI
                      </Button>
                    )}
                  </div>
                ),
              },
              {
                key: 'evidence',
                label: `Evidence${context.evidence?.length ? ` · ${context.evidence.length}` : ''}`,
                children:
                  context.evidence && context.evidence.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {context.evidence.map((hash, i) => (
                        <div
                          className="ol-mono"
                          key={hash || i}
                          style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)', wordBreak: 'break-all' }}
                          title={hash}
                        >
                          {hash}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12.5 }}>
                      No evidence is bound to this item. Evidence appears here as content addresses
                      only — the drawer never renders a raw payload.
                    </Text>
                  ),
              },
              {
                key: 'decisions',
                label: (
                  <span>
                    Decisions{context.decisions?.length ? ` · ${context.decisions.length}` : ''}{' '}
                    <span className="ol-kbd" aria-hidden>
                      ]
                    </span>
                  </span>
                ),
                children:
                  context.decisions && context.decisions.length > 0 ? (
                    <DecisionsPanel decisions={context.decisions} />
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12.5 }}>
                      Nothing has been decided on this run or case yet. Your disposition below will
                      be the first entry in the decision ledger.
                    </Text>
                  ),
              },
            ]}
          />

          {/* Disposition — PINNED. The action zone an analyst hits hundreds of times a shift stays
              in one place whatever the context tab shows (Fitts's Law). */}
          <div className="ol-wb-dispose">
            <div className="ol-section" style={{ justifyContent: 'space-between' }}>
              <span>Disposition</span>
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                <span className="ol-kbd" aria-hidden>
                  n
                </span>{' '}
                note
              </span>
            </div>
            <DispositionPanel
              item={item}
              context={context}
              onOpenApprovals={openApprovals}
              onDone={() => {
                void reload();
                onChanged();
              }}
            />
          </div>

          {item.entityId && (
            <RfiComposer
              open={rfiOpen}
              entityId={item.entityId}
              workItemId={item.itemId}
              onClose={() => setRfiOpen(false)}
              onChanged={() => setRfiOpen(false)}
            />
          )}
        </div>
      )}
    </Drawer>
  );
}
